import React, { useLayoutEffect, useRef, useState } from "react";
import { Box, ButtonBase, Popover, Stack, Typography, alpha, useTheme } from "@mui/material";
import ManageSearchRoundedIcon from "@mui/icons-material/ManageSearchRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { AppButton } from "../../../components/form";

export type ChatScopeBubbleKind = "index" | "adaptor" | "openapi" | "output" | "candidate" | "suggestions";

export type ChatScopeBubbleItem = {
  key: string;
  kind: ChatScopeBubbleKind;
  /** Full resource name shown in the detail popover. */
  title: string;
  /** Kind / selection mode line under the title, e.g. "Index · Auto-selected". */
  subtitle?: string;
  details?: { label: string; value: string }[];
  /** Auto-selected items render filled; manual ones outlined. */
  auto?: boolean;
  /** Small counter on the bubble (e.g. number of suggestions). */
  count?: number;
  /** Custom popover body; receives a closer so list rows can dismiss the popover. */
  content?: (close: () => void) => React.ReactNode;
  actions?: { label: string; onClick: () => void; color?: "primary" | "error" | "warning"; variant?: "contained" | "outlined" | "text" }[];
};

const KIND_ICON: Record<ChatScopeBubbleKind, React.ElementType> = {
  index: ManageSearchRoundedIcon,
  adaptor: StorageRoundedIcon,
  openapi: HubRoundedIcon,
  output: InsightsRoundedIcon,
  candidate: LightbulbRoundedIcon,
  suggestions: AutoAwesomeRoundedIcon,
};

/** Single-line text that scrolls horizontally back and forth when it is wider than its box. */
function MarqueeText({ text, sx }: { text: string; sx?: React.ComponentProps<typeof Typography>["sx"] }) {
  const boxRef = useRef<HTMLSpanElement | null>(null);
  const textRef = useRef<HTMLSpanElement | null>(null);
  const [overflow, setOverflow] = useState(0);

  useLayoutEffect(() => {
    const box = boxRef.current;
    const inner = textRef.current;
    if (!box || !inner) return;
    const measure = () => setOverflow(Math.max(0, Math.ceil(inner.scrollWidth - box.clientWidth)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [text]);

  // ~40px/s, with pauses at both ends (built into the keyframes).
  const duration = Math.max(4, (overflow / 40) * 2 + 2);

  return (
    <Typography
      ref={boxRef}
      component="span"
      title={text}
      sx={{
        display: "block",
        overflow: "hidden",
        whiteSpace: "nowrap",
        minWidth: 0,
        ...(overflow > 0
          ? {
              maskImage: "linear-gradient(90deg, transparent 0, #000 10px, #000 calc(100% - 10px), transparent 100%)",
              WebkitMaskImage: "linear-gradient(90deg, transparent 0, #000 10px, #000 calc(100% - 10px), transparent 100%)",
            }
          : {}),
        ...sx,
      }}
    >
      <Box
        ref={textRef}
        component="span"
        sx={{
          display: "inline-block",
          px: overflow > 0 ? "10px" : 0,
          animation: overflow > 0 ? `chatScopeMarquee ${duration}s ease-in-out infinite` : "none",
          "@keyframes chatScopeMarquee": {
            "0%, 15%": { transform: "translateX(0)" },
            "50%, 65%": { transform: `translateX(-${overflow}px)` },
            "100%": { transform: "translateX(0)" },
          },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      >
        {text}
      </Box>
    </Typography>
  );
}

interface Props {
  items: ChatScopeBubbleItem[];
  size?: "small" | "medium";
}

/** Animated icon bubbles for chat scope selections; clicking one opens its details. */
export default function ChatScopeBubbles({ items, size = "medium" }: Props) {
  const theme = useTheme();
  const [anchor, setAnchor] = useState<{ el: HTMLElement; key: string } | null>(null);
  const active = anchor ? items.find((item) => item.key === anchor.key) : undefined;
  const dim = size === "small" ? 26 : 32;
  const close = () => setAnchor(null);

  const colorFor = (item: ChatScopeBubbleItem) => {
    if (item.kind === "candidate") return theme.palette.warning.main;
    if (item.kind === "output") return theme.palette.secondary.main;
    if (item.auto) return theme.palette.info.main;
    return theme.palette.primary.main;
  };

  if (items.length === 0) return null;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          gap: size === "small" ? 0.5 : 0.75,
          flexShrink: 0,
          maxWidth: "100%",
        }}
      >
        {items.map((item, i) => {
          const Icon = KIND_ICON[item.kind];
          const color = colorFor(item);
          const filled = item.auto || item.kind === "suggestions";
          const selected = anchor?.key === item.key;
          return (
            <ButtonBase
              key={item.key}
              aria-label={item.subtitle ? `${item.subtitle}: ${item.title}` : item.title}
              title={item.title}
              onClick={(e) => setAnchor(selected ? null : { el: e.currentTarget, key: item.key })}
              sx={{
                position: "relative",
                width: dim,
                height: dim,
                borderRadius: "50%",
                flexShrink: 0,
                color: filled ? theme.palette.getContrastText(color) : color,
                bgcolor: filled ? color : alpha(color, 0.1),
                border: `1.5px ${item.kind === "candidate" ? "dashed" : "solid"} ${alpha(color, filled ? 0 : 0.7)}`,
                boxShadow: selected ? `0 0 0 3px ${alpha(color, 0.3)}` : `0 2px 6px ${alpha(color, 0.25)}`,
                transition: "transform 180ms cubic-bezier(.34,1.56,.64,1), box-shadow 180ms",
                "&:hover": { transform: "scale(1.15)" },
                animation: `chatScopeBubblePop 420ms cubic-bezier(.34,1.56,.64,1) ${i * 60}ms backwards, chatScopeBubbleFloat 3.2s ease-in-out ${600 + i * 280}ms infinite`,
                "&::after":
                  item.kind === "candidate" || item.kind === "suggestions"
                    ? {
                        content: '""',
                        position: "absolute",
                        inset: -3,
                        borderRadius: "50%",
                        border: `2px solid ${alpha(color, 0.5)}`,
                        animation: "chatScopeBubblePulse 2.2s ease-out infinite",
                        pointerEvents: "none",
                      }
                    : undefined,
                "@keyframes chatScopeBubblePop": {
                  "0%": { opacity: 0, transform: "scale(0.2) translateX(-8px)" },
                  "70%": { opacity: 1, transform: "scale(1.12)" },
                  "100%": { opacity: 1, transform: "scale(1)" },
                },
                "@keyframes chatScopeBubbleFloat": {
                  "0%, 100%": { translate: "0 0" },
                  "50%": { translate: "0 -3px" },
                },
                "@keyframes chatScopeBubblePulse": {
                  "0%": { opacity: 0.9, transform: "scale(0.9)" },
                  "100%": { opacity: 0, transform: "scale(1.45)" },
                },
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                  "&::after": { animation: "none", opacity: 0 },
                },
              }}
            >
              <Icon sx={{ fontSize: size === "small" ? 15 : 18 }} />
              {item.count ? (
                <Box
                  component="span"
                  sx={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    minWidth: 16,
                    height: 16,
                    px: 0.4,
                    borderRadius: 8,
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    lineHeight: "16px",
                    bgcolor: theme.palette.background.paper,
                    color,
                    border: `1px solid ${alpha(color, 0.6)}`,
                  }}
                >
                  {item.count}
                </Box>
              ) : null}
            </ButtonBase>
          );
        })}
      </Box>

      <Popover
        open={Boolean(anchor && active)}
        anchorEl={anchor?.el}
        onClose={close}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: -1,
              p: 1.5,
              borderRadius: "14px",
              width: "min(92vw, 300px)",
              boxSizing: "border-box",
              // The theme makes popover papers transparent; this one floats over chat text.
              bgcolor: "background.paper",
              backgroundImage: "none",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[8],
            },
          },
        }}
      >
        {active && (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              {(() => {
                const Icon = KIND_ICON[active.kind];
                const color = colorFor(active);
                return (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      flexShrink: 0,
                      bgcolor: alpha(color, 0.14),
                      color,
                    }}
                  >
                    <Icon sx={{ fontSize: 18 }} />
                  </Box>
                );
              })()}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <MarqueeText text={active.title} sx={{ typography: "body2", fontWeight: 600 }} />
                {active.subtitle && (
                  <Typography variant="caption" color="text.secondary">
                    {active.subtitle}
                  </Typography>
                )}
              </Box>
            </Stack>
            {active.details?.map((d) => (
              <Stack key={d.label} direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {d.label}
                </Typography>
                <Box sx={{ minWidth: 0, display: "flex", justifyContent: "flex-end" }}>
                  <MarqueeText text={d.value} sx={{ typography: "caption", fontWeight: 600 }} />
                </Box>
              </Stack>
            ))}
            {active.content?.(close)}
            {active.actions && active.actions.length > 0 && (
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                {active.actions.map((a) => (
                  <AppButton
                    key={a.label}
                    size="small"
                    color={a.color ?? "primary"}
                    variant={a.variant ?? "outlined"}
                    onClick={() => {
                      close();
                      a.onClick();
                    }}
                  >
                    {a.label}
                  </AppButton>
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Popover>
    </>
  );
}
